import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { fromEvent, Subject } from 'rxjs';
import { delay, filter } from 'rxjs/operators';
import { bumpIn } from '../animations';
import { DarkThemeSwitch } from '../dark-theme-switch';

@Component({
	selector: 'app-icon-button',
	templateUrl: './icon-button.component.html',
	styleUrls: ['./icon-button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [bumpIn],
})
export class IconButtonComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('iconButtonRef', { static: true }) iconButtonRef: ElementRef;

	@Input() size = 25;
	@Input() onDarkShade = false;
	@Input() gray500: boolean;
	@Input() srcString = '';
	@Input() pressed = false;
	@Input() switchColor = true;
	@Input() eventBubbling = false;
	@Input() blueWhite: boolean;
	@Input() hasShadow = true;
	@Input() notificationBadge = false;
	@Input() notificationCount: number;
	@Input() color = 'Blue-Gray';
	@Input() newIconButton: boolean;
	@Input() backgroundColor: string;
	@Output() clickEvent: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

	hovered = false;
	down = false;
	selected = false;

	private destroyer$: Subject<any> = new Subject();

	constructor(private darkTheme: DarkThemeSwitch, private sanitizer: DomSanitizer, private cdr: ChangeDetectorRef) {}

	get src() {
		let lightFill;

		if (this.onDarkShade) {
			lightFill = 'White';
		} else if (this.blueWhite) {
			lightFill = 'Blue-White';
		} else {
			if (this.selected && this.switchColor) {
				lightFill = 'Jade';
			} else {
				lightFill = this.color;
			}
		}

		return this.darkTheme.getIcon({
			iconName: this.srcString,
			darkFill: this.blueWhite ? 'Blue-White' : this.selected ? 'Jade' : 'Navy',
			lightFill: lightFill,
			gray500: this.gray500,
		});
	}

	get opacity() {
		let opacity;
		if (this.onDarkShade) {
			if (this.hovered || this.pressed) {
				opacity = '.8';
			} else {
				opacity = '1';
			}
		} else {
			if (this.hovered && !this.down) {
				opacity = '.8';
			} else {
				opacity = '1';
			}
		}
		return this.sanitizer.bypassSecurityTrustStyle(`${opacity}`);
	}

	get bgc() {
		let alphaChannel: number;
		let rgb: string;
		if (this.onDarkShade) {
			alphaChannel = 0.5;
			if (this.hovered && !this.down) {
				alphaChannel = 0.5;
			} else {
				alphaChannel = 0.25;
			}
			rgb = '247, 247, 247, ';
		} else {
			if (this.hovered && !this.down) {
				alphaChannel = 0.75;
			} else {
				alphaChannel = 0.5;
			}
			rgb = '244, 244, 244, ';
		}
		return this.sanitizer.bypassSecurityTrustStyle(`rgba(${rgb}${this.hasShadow ? alphaChannel : 0}`);
	}

	get newbgc() {
		if (this.hovered) {
			if (this.down) {
				if (this.selected && this.switchColor) {
					return '#C6ECDF';
				}
				return '#E2E6EC';
			}
			if (this.selected) {
				return '#D9F4EB';
			}
			return '#EAEDF1';
		}
		if (this.selected && this.switchColor) {
			return '#E5F7F1';
		}
		if (this.backgroundColor) {
			return this.backgroundColor;
		}
		return '#F0F2F5';
	}

	ngOnInit() {
		fromEvent(document.body, 'click')
			.pipe(
				delay(200),
				filter(() => !!this.selected)
			)
			.subscribe(() => {
				this.hovered = false;
				this.selected = false;
				this.cdr.detectChanges();
			});
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	ngAfterViewInit(): void {}

	onClick(evt) {
		this.pressed = !this.pressed;
		this.selected = !this.selected;
		if (!this.eventBubbling) {
			evt.stopPropagation();
		}
		this.clickEvent.emit(evt);
	}

	ngOnDestroy(): void {
		this.destroyer$.next(undefined);
		this.destroyer$.complete();
	}
}
